/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { getAllUsersInSpace } from "@Actions/projectUsers";
import type { RootState } from "src/reducers";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import IconComponent from "@CubeComponents/icons";
import { setModulePermissions } from "@Components/ModuleUsersAccess/constants";
import type { SpaceUserFromAllUsersInSpace } from "@ServerTypes/accounts/accounts.schema";

type ModulePermissionEntry = { role?: string };
type SpaceUser = SpaceUserFromAllUsersInSpace & {
  // ADDED: optional `modulePermissions` keyed by module. Backend schema currently
  // types this as z.any(); the frontend reads `modulePermissions[module].role`.
  modulePermissions?: { [moduleKey: string]: ModulePermissionEntry };
};
type ModuleRoleOption = ReturnType<typeof setModulePermissions>[number];

type PermissionFlowProps = {
  spaceId: string;
  getUsersCallBack: (usersRoles: { [userId: string]: string }) => void;
  module: "ESTIMATES" | "CONTRACTS" | "SCHEDULES";
};

function PermissionFlow({ spaceId, getUsersCallBack, module }: Readonly<PermissionFlowProps>) {
  const { t } = useTranslation();
  const spaceUsers = useSelector((state: RootState) => state.spaceUsers);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const typedSpaceUsers = spaceUsers as SpaceUser[];
  const [bulkSelectedRole, setBulkSelectedRole] = useState("");
  const [usersRoles, setUsersRoles] = useState<{ [userId: string]: string }>({});

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllUsersInSpace({ spaceId }));
  }, [spaceId]);

  useEffect(() => {
    setRoleForAllUsers();
  }, [spaceUsers]);

  useEffect(() => {
    if (Object.keys(usersRoles).length) {
      getUsersCallBack(usersRoles);
    }
  }, [usersRoles]);

  const handleBulkRuleChange = (option: ModuleRoleOption) => {
    // @ts-expect-error -- a numeric role id is stored into state declared `string`. REAL GAP: the state type and the id type disagree; one of the two is wrong.
    setBulkSelectedRole(option.id);
  };

  const setRoleForAllUsers = (bulkSelectedRole?: string) => {
    const allSelectedUserRole: { [userId: string]: string } = { ...usersRoles };
    typedSpaceUsers.map((data: SpaceUser) => {
      allSelectedUserRole[data.id] = bulkSelectedRole || data.modulePermissions?.[module]?.role || "";
    });
    setUsersRoles(allSelectedUserRole);
  };

  const setUserRole = (option: ModuleRoleOption, userId: string) => {
    const allUserRoles: { [userId: string]: string } = { ...usersRoles };
    // @ts-expect-error -- same numeric-role-id-into-string-state mismatch as line 54.
    allUserRoles[userId] = option.id;
    setUsersRoles(allUserRoles);
  };

  const itemTemplate = (data: ModuleRoleOption) => {
    return (
      <div className="flex gap-3 p-1 items-center">
        <div>
          <IconComponent name={data.name} width="1.5rem" height="1.5rem" />
        </div>
        <div className="flex flex-col">
          <div className="font-semibold">{data.frontendName}</div>
          <div className="text-xs">{data.description}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-2 h-125 flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <span className="font-semibold">{t("Select bulk permission to users")}</span>
        <div className="flex gap-3 items-center border border-[#C6D8EC] p-4">
          <span className="whitespace-nowrap font-semibold">{t("Permission")}</span>
          <SelectDropDown
            renderOption={itemTemplate}
            // @ts-expect-error -- the option handler is written against `ModuleRoleOption` but the picker declares its value as the option object or an array of them, plus `null` when cleared.
            onChange={handleBulkRuleChange}
            value={bulkSelectedRole}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            fields={{ text: "frontendName", value: "id" }}
            options={setModulePermissions(module)}
            placeholder={t("Select Role")}
            buttonWidth="100%"
            popupAlign="right"
            popperWidth={400}
          />
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            disabled={!bulkSelectedRole}
            onClick={() => setRoleForAllUsers(bulkSelectedRole)}
          >
            {t("Bulk update")}
          </ButtonComponent>
        </div>
      </div>
      <div className="flex flex-col gap-5 overflow-auto">
        <div className="flex items-center gap-2">
          <span className="font-semibold whitespace-nowrap">{t(".. or update user permission individually")}</span>
        </div>
        <div className="flex flex-col gap-4 overflow-auto">
          <div className="flex">
            <span className="flex-1 font-semibold">{t("Users")}</span>
            <span className="flex-1 font-semibold">{t("Permissions")}</span>
          </div>
          {typedSpaceUsers.map(
            (user: SpaceUser) =>
              user.id !== userDetails._id && (
                <div key={user.id} className="flex items-center">
                  <span className="flex-1">
                    <UserNameTagComponent id={user.id} />
                  </span>
                  <span className="flex-1">
                    <SelectDropDown
                      renderOption={itemTemplate}
                      onChange={(value) => {
                        if (value && !Array.isArray(value)) setUserRole(value as ModuleRoleOption, user.id);
                      }}
                      value={(usersRoles[user.id] ?? null) as unknown as ModuleRoleOption | null}
                      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                      fields={{ text: "frontendName", value: "id" }}
                      options={setModulePermissions(module)}
                      placeholder={t("Select Project Role")}
                      buttonWidth="100%"
                      popupAlign="right"
                      popperWidth={400}
                    />
                  </span>
                </div>
              ),
          )}
        </div>
      </div>
    </div>
  );
}

export default PermissionFlow;
